import React, { useState } from 'react';
import PropTypes from 'prop-types';
import api from '@utils/axios';
import I18n, { translate } from '@utils/i18n';
import { photoPath, buildingPath, customerPortalBuildingPath } from '@routes';
import { Text } from '@ui/atoms/Text';
import { Checkbox } from '@ui/molecules/inputs/Checkbox';
import { actionTypes } from '../reducer';
import InputTextArea from '../../../components/InputTextArea/InputTextArea';
import { usePhotoGalleryContext } from '../PhotoGalleryContext';

const namespace = 'features.photo_gallery.photo_detail';

function PhotoDetail({ photo, yearMonth, dispatch }) {
  const { isCustomerPortal } = usePhotoGalleryContext();
  const [description, setDescription] = useState(photo.description);
  const [internal, setInternal] = useState(photo.internal);
  const [loading, setLoading] = useState(false);

  const onSave = async (newDescription) => {
    setLoading(true);
    await api
      .put(photoPath({ id: photo.id, format: 'json', type: photo.type, _options: true }), {
        photo: { caption: newDescription, is_internal: internal }
      })
      .then(({ data }) => dispatch({ type: actionTypes.PHOTO_UPDATED, newPhoto: data, yearMonth }))
      .then(() => {
        setDescription(newDescription);
        document.querySelector('div.yarl__container.yarl__flex_center').focus();
      });
    setLoading(false);
  };

  const onCancelClick = () => {
    setInternal(photo.internal);
    document.querySelector('div.yarl__container.yarl__flex_center').focus();
  };

  return (
    <>
      <div className="media__major">
        <img className="image_detail" src={photo.large} alt={photo.description} title={photo.description} />
      </div>
      <div className="media__aside">
        <div className="media__section--details">
          {isCustomerPortal ? (
            <div className="content__item">
              <span className="content__label">{translate('description', { namespace })}</span>
              <span className="content__value">{description ?? '--'}</span>
            </div>
          ) : (
            <InputTextArea
              onCancel={onCancelClick}
              editLeftControl={
                <Checkbox
                  className="qmb-checkbox__input"
                  id="internal_only"
                  checked={internal}
                  onChange={() => setInternal(!internal)}
                  label={translate('internal_only', { namespace })}
                />
              }
              leftControl={
                photo.internal && (
                  <div className="qmb-checkbox" style={{ marginRight: 'auto' }}>
                    <label htmlFor="internal_only">{translate('internal_only', { namespace })}</label>
                  </div>
                )
              }
              rightControl={
                <em>
                  {translate('updated_on', { namespace })} {new Date(photo.updatedAt).toLocaleDateString('en-US')}
                </em>
              }
              loading={loading}
              id="photo_description"
              editable
              value={description ?? ''}
              onSave={onSave}
              onChange={setDescription}
              label="Description"
            />
          )}
        </div>

        <hr className="media__split" />

        <div className="media__section">
          <div className="content__item">
            <span className="content__label">{translate('photo_type', { namespace })}</span>
            <span className="content__value">{photo.photoType}</span>
          </div>
          <div className="content__item">
            <span className="content__label">{translate('date_taken', { namespace })}</span>
            <span className="content__value">{new Date(photo.dateTaken).toLocaleDateString('en-US')}</span>
          </div>
          {photo.building && (
            <div className="content__item">
              <span className="content__label">{I18n.t('generic.building')}</span>
              <span className="content__value">
                <Text
                  variant="link"
                  as="a"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={
                    isCustomerPortal ? customerPortalBuildingPath(photo.building.id) : buildingPath(photo.building.id)
                  }>
                  {photo.building.name}
                </Text>
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

PhotoDetail.propTypes = {
  dispatch: PropTypes.func.isRequired,
  yearMonth: PropTypes.string.isRequired,
  photo: PropTypes.object.isRequired
};

export default PhotoDetail;
